import { useMemo } from 'react';
import { useEditorSelector, editorStore } from '../../editorStore';
import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';
import ItemImageField from './ItemImageField';

const LANGUAGES = ['en', 'fr', 'de', 'es', 'it', 'pt', 'nl'] as const;

export default function ContentPanel({ ctx }: { ctx: PanelCtx }) {
  const meta = useEditorSelector((state) => state.site?.meta);
  const language = meta?.language?.split('-')[0] ?? 'en';
  const languages = useMemo(() => LANGUAGES, []);

  const handleTitleChange = (value: string) => {
    editorStore.updateMeta({ title: value });
  };

  const handleDescriptionChange = (value: string) => {
    editorStore.updateMeta({ description: value });
  };

  const handleLanguageChange = (value: string) => {
    editorStore.updateMeta({ language: value });
  };

  return (
    <div className="dw-panel">
      <div className="dw-panel-heading">
        <Icon name="type" size={18} />
        <h2>Content</h2>
      </div>
      <p className="dw-help-text">
        Click any text in the preview to edit it. Enter saves, Esc cancels.
      </p>

      <section className="dw-card">
        <h3 className="dw-card-title">Site details</h3>
        <div className="dw-field">
          <label className="dw-label" htmlFor="dw-site-title">
            Site title
          </label>
          <input
            id="dw-site-title"
            className="dw-input"
            type="text"
            value={meta?.title ?? ''}
            onChange={(e) => handleTitleChange(e.target.value)}
          />
        </div>
        <div className="dw-field">
          <label className="dw-label" htmlFor="dw-site-description">
            Description
          </label>
          <textarea
            id="dw-site-description"
            className="dw-input dw-textarea"
            value={meta?.description ?? ''}
            onChange={(e) => handleDescriptionChange(e.target.value)}
          />
        </div>
        <div className="dw-field">
          <label className="dw-label" htmlFor="dw-site-language">
            Language
          </label>
          <select
            id="dw-site-language"
            className="dw-input dw-select"
            value={languages.includes(language as (typeof LANGUAGES)[number]) ? language : 'en'}
            onChange={(e) => handleLanguageChange(e.target.value)}
          >
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
        <div className="dw-field">
          <ItemImageField
            label="Site icon"
            assetId={ctx.props.favicon?.assetId ?? ''}
            ctx={ctx}
            onChange={(assetId) => ctx.update({ favicon: assetId ? { assetId } : undefined })}
          />
          <p className="dw-help-text">The small picture in browser tabs. A square image works best.</p>
        </div>
      </section>

      <section className="dw-tip-grid">
        <div className="dw-tip-card">
          <Icon name="type" size={20} />
          <h3>Edit headlines</h3>
          <p>Click any title or paragraph in the preview, then simply type.</p>
        </div>
        <div className="dw-tip-card">
          <Icon name="image" size={20} />
          <h3>Swap artwork</h3>
          <p>Click an illustration or photo area to open its settings in the Images tab.</p>
        </div>
      </section>
    </div>
  );
}
